import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HostService, HostVehicle } from './host.service';
import { ToastMessage } from '../toast-message/toast-message.service';
import { DataService } from '../data.service';
import { ConstantsService } from '../constants.service';

interface Mode {
  _id: string;
  mode: string;
}

interface VehicleModel {
  _id: string;
  model: string;
  manufacturer: string;
  displayName?: string;
  modeId: string;
}

interface EditState {
  status: string;
  selectedModeId: string;
  selectedModelId: string;
  remarks: string;
  vehicleModels: VehicleModel[];
  isLoadingModels: boolean;
  isSaving: boolean;
  // RC image upload within the approve flow
  uploadImageData: string;
  isUploadingImage: boolean;
  uploadDone: boolean;
}

@Component({
  selector: 'app-host-detail',
  template: require('./host-detail.component.html')
})
export class HostDetailComponent implements OnInit {
  hostId = '';
  hostName = '';
  hostMobile = '';
  highlightVehicleId = '';
  vehicles: HostVehicle[] = [];
  isLoading = false;

  modeNameMap: { [id: string]: string } = {};
  hostModes: Mode[] = [];
  isLoadingModes = true;

  editStates: { [rcId: string]: EditState } = {};

  canEdit = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private hostService: HostService,
    private toast: ToastMessage,
    private dataService: DataService,
    private constantService: ConstantsService
  ) {}

  private checkEditAccess() {
    const editRole = this.constantService.hostPermissions.edit;
    const accessRoles = this.dataService.accessCookie('accessRoles') || '';
    const roles = accessRoles ? accessRoles.split(',') : [];
    this.canEdit = roles.indexOf(editRole) > -1;
  }

  ngOnInit() {
    this.hostId = this.route.snapshot.paramMap.get('hostId') || '';
    this.highlightVehicleId = this.route.snapshot.queryParamMap.get('highlight') || '';
    this.hostName = this.route.snapshot.queryParamMap.get('name') || '';
    this.hostMobile = this.route.snapshot.queryParamMap.get('mobile') || '';
    this.checkEditAccess();

    if (this.hostId) {
      this.loadVehicles();
      this.loadModesAndFilter();
    }
  }

  loadVehicles() {
    this.isLoading = true;
    this.hostService.getHostVehicles(this.hostId).subscribe(
      (vehicles) => { this.isLoading = false; this.vehicles = vehicles; },
      (err) => {
        this.isLoading = false;
        this.toast.openSnackBarError(err && err.error && err.error.message || 'Failed to load vehicles');
      }
    );
  }

  loadModesAndFilter() {
    this.isLoadingModes = true;
    this.hostService.getHostConfig().subscribe(
      (cfg) => {
        const validIds = new Set(cfg.validPoolModeIds);
        this.hostService.getModes(this.hostId).subscribe(
          (modes) => {
            this.isLoadingModes = false;
            this.modeNameMap = {};
            modes.forEach((m: Mode) => { this.modeNameMap[m._id] = m.mode; });
            this.hostModes = modes.filter(m => validIds.has(m._id));
          },
          () => { this.isLoadingModes = false; }
        );
      },
      () => { this.isLoadingModes = false; }
    );
  }

  getModeNameById(modeId: string): string {
    if (!modeId) return '';
    return this.modeNameMap[modeId] || '';
  }

  getModeIcon(modeName: string): string {
    if (!modeName) return 'directions_car';
    const n = modeName.toLowerCase();
    if (n.includes('bike') || n.includes('two') || n.includes('moto') || n.includes('cycle') || n.includes('scooty')) {
      return 'two_wheeler';
    }
    return 'directions_car';
  }

  openEdit(vehicle: HostVehicle) {
    const existingModeId = (vehicle.info && vehicle.info.modeId) || '';
    const existingModelId = (vehicle.info && vehicle.info.vehicleModel) || '';
    this.editStates[vehicle.rcId] = {
      status: vehicle.status || 'processing',
      selectedModeId: existingModeId,
      selectedModelId: existingModelId,
      remarks: '',
      vehicleModels: [],
      isLoadingModels: false,
      isSaving: false,
      uploadImageData: '',
      isUploadingImage: false,
      uploadDone: false,
    };
    if (existingModeId) {
      this.loadModelsForEdit(vehicle.rcId, existingModeId, existingModelId);
    }
  }

  cancelEdit(rcId: string) {
    delete this.editStates[rcId];
  }

  isEditing(rcId: string): boolean {
    return !!this.editStates[rcId];
  }

  onEditStatusChanged(rcId: string) {
    const s = this.editStates[rcId];
    if (!s) return;
    if (s.status === 'rejected') {
      s.remarks = '';
    }
    if (s.status === 'approved' && s.selectedModeId && s.vehicleModels.length === 0) {
      this.loadModelsForEdit(rcId, s.selectedModeId, s.selectedModelId);
    }
  }

  onEditModeSelected(rcId: string) {
    const s = this.editStates[rcId];
    if (!s || !s.selectedModeId) return;
    s.selectedModelId = '';
    this.loadModelsForEdit(rcId, s.selectedModeId, '');
  }

  loadModelsForEdit(rcId: string, modeId: string, preselectModelId: string) {
    const s = this.editStates[rcId];
    if (!s) return;
    s.isLoadingModels = true;
    s.vehicleModels = [];
    this.hostService.getVehicleModels(modeId).subscribe(
      (models) => {
        s.vehicleModels = models;
        s.isLoadingModels = false;
        if (preselectModelId) {
          const found = models.some((m: VehicleModel) => m._id === preselectModelId);
          s.selectedModelId = found ? preselectModelId : '';
        }
      },
      () => { s.isLoadingModels = false; this.toast.openSnackBarError('Failed to load vehicle models'); }
    );
  }

  onRcImageSelected(rcId: string, event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) return;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const s = this.editStates[rcId];
      if (!s) return;
      s.uploadImageData = e.target.result;
      s.uploadDone = false;
      this.uploadRcImage(rcId);
    };
    reader.readAsDataURL(input.files[0]);
  }

  uploadRcImage(rcId: string) {
    const s = this.editStates[rcId];
    if (!s || !s.uploadImageData) return;
    s.isUploadingImage = true;
    this.hostService.uploadRcImage(rcId, s.uploadImageData).subscribe(
      () => {
        s.isUploadingImage = false;
        s.uploadDone = true;
        this.toast.openSnackBarSuccess('RC image uploaded');
      },
      (err) => {
        s.isUploadingImage = false;
        s.uploadDone = false;
        this.toast.openSnackBarError(err && err.error && err.error.message || 'Failed to upload image');
      }
    );
  }

  saveEdit(vehicle: HostVehicle) {
    const s = this.editStates[vehicle.rcId];
    if (!s) return;

    if (s.status === 'approved') {
      if (!s.selectedModelId) {
        this.toast.openSnackBarError('Please select a vehicle model');
        return;
      }
      s.isSaving = true;
      this.hostService.approveVehicle(this.hostId, vehicle.rcId, {
        vehicleNumber: vehicle.number,
        vehicleModel: {
          id: s.selectedModelId,
          modeId: s.selectedModeId || undefined,
        },
        status: 'approved',
      }).subscribe(
        () => {
          s.isSaving = false;
          this.toast.openSnackBarSuccess('Vehicle approved');
          delete this.editStates[vehicle.rcId];
          this.loadVehicles();
        },
        (err) => {
          s.isSaving = false;
          this.toast.openSnackBarError(err && err.error && err.error.message || 'Failed to approve vehicle');
        }
      );
    } else if (s.status === 'rejected') {
      if (!s.remarks.trim()) {
        this.toast.openSnackBarError('Please enter rejection remarks');
        return;
      }
      s.isSaving = true;
      this.hostService.rejectVehicle(this.hostId, vehicle.rcId, {
        vehicleNumber: vehicle.number,
        status: 'rejected',
        remarks: s.remarks,
      }).subscribe(
        () => {
          s.isSaving = false;
          this.toast.openSnackBarSuccess('Vehicle rejected');
          delete this.editStates[vehicle.rcId];
          this.loadVehicles();
        },
        (err) => {
          s.isSaving = false;
          this.toast.openSnackBarError(err && err.error && err.error.message || 'Failed to reject vehicle');
        }
      );
    }
  }

  isHighlighted(vehicle: HostVehicle): boolean {
    return !!this.highlightVehicleId && vehicle.rcId === this.highlightVehicleId;
  }

  statusLabel(status: string): string {
    const m: { [k: string]: string } = { approved: 'Approved', rejected: 'Rejected', processing: 'Processing' };
    return m[status] || 'Processing';
  }

  vehicleLabel(v: HostVehicle): string {
    if (!v || !v.info) return '';
    if (v.info.displayName) return v.info.displayName;
    const parts = [v.info.manufacturer, v.info.model].filter(Boolean);
    return parts.join(' ');
  }

  goBack() {
    this.router.navigate(['/hostSearch']);
  }
}
